import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";

/** The party object returned by POST /queue/party/join. */
type PartyResponse = Awaited<ReturnType<typeof queueApi.joinParty>>;

/**
 * Join an existing party by its shareable code (POST /queue/party/join).
 *
 * The joining member is the token user (server-derived); the only input is the
 * code. On success the caller navigates to the joined party's detail page.
 * Errors are NOT swallowed — a 404 (no open party with that code), 409 (already
 * in a party / full), or 403 surfaces on `mutation.error` for the page to map.
 */
export function useJoinParty(facilityId?: string) {
  const queryClient = useQueryClient();
  return useMutation<PartyResponse, unknown, string>({
    mutationFn: (code: string) => queueApi.joinParty(code),
    onSettled: () => {
      if (!facilityId) return;
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.session(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.facility(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: ["dropin", "my-entry", facilityId],
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.myParty(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.partyMemberships(facilityId),
        refetchType: "active",
      });
    },
  });
}
